import React, { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";

import ConfigurableFieldDialog from "../settings/ConfigurableFieldDialog";
import EditConfigurableFieldDialog from "../settings/EditConfigurableFieldDialog";

export default function ManageConfigurableFieldsDialog({ fields, onClose }) {
  const [activeStep, setActiveStep] = useState("choice");
  const [editingField, setEditingField] = useState(null);
  const [fieldSelectorOpen, setFieldSelectorOpen] = useState(false);
  const sortedFields = [...fields].sort((a, b) =>
    a.defaultLabel.localeCompare(b.defaultLabel)
  );

  if (activeStep === "create") {
    return (
      <ConfigurableFieldDialog
        enabledTemplateIds={sortedFields.map(field => field.id)}
        onClose={() => setActiveStep("choice")}
      />
    );
  }

  if (activeStep === "edit" && editingField) {
    return (
      <EditConfigurableFieldDialog
        customFieldTemplate={editingField}
        onClose={() => {
          setEditingField(null);
          setActiveStep("choice");
        }}
      />
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ position: "relative", textAlign: "center", py: 2 }}>
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Manage Configurable Fields
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 4 }}>
        {activeStep === "choice" ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Typography color="textSecondary">
              Would you like to add a new field or edit an existing one?
            </Typography>
            <Box display="flex" flexDirection="column" width="100%" maxWidth={320} gap={1.5}>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setActiveStep("create")}
              >
                Add New Field
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<EditOutlinedIcon />}
                onClick={() => {
                  setFieldSelectorOpen(true);
                  setActiveStep("select");
                }}
              >
                Edit Existing Field
              </Button>
            </Box>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setFieldSelectorOpen(false);
                setActiveStep("choice");
              }}
              sx={{ alignSelf: "flex-start" }}
            >
              Back
            </Button>
            <Typography color="textSecondary">
              Select the field you would like to edit.
            </Typography>
            <MuiAutocomplete
              autoHighlight
              open={fieldSelectorOpen}
              openOnFocus
              options={sortedFields}
              getOptionLabel={field => field.defaultLabel}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onOpen={() => setFieldSelectorOpen(true)}
              onClose={() => setFieldSelectorOpen(false)}
              onChange={(_event, field) => {
                if (!field) return;

                setFieldSelectorOpen(false);
                setEditingField(field);
                setActiveStep("edit");
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  autoFocus
                  label="Configurable Field"
                  placeholder="Search fields"
                />
              )}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
